import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import type { ChapterInfo } from '@/features/reader/useReadingPath';

interface ChapterTOCProps {
  isOpen: boolean;
  onClose: () => void;
  chapters: ChapterInfo[];
  currentNodeId: string | null;
  onSelectChapter: (nodeId: string, index: number) => void;
}

export function ChapterTOC({
  isOpen,
  onClose,
  chapters,
  currentNodeId,
  onSelectChapter,
}: ChapterTOCProps) {
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close on Escape key - only attach listener when open
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="relative w-80 max-w-[80vw] bg-reader-bg border-r border-reader-border flex flex-col transform transition-transform duration-250"
        style={{ transform: 'translateX(0)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Chapter list"
      >
        {/* Library button */}
        <div className="pt-14 pb-2 px-3">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-reader-text-heading hover:bg-reader-bg-card transition-colors"
            onClick={onClose}
          >
            <span className="text-xl">📚</span>
            <span className="text-lg font-semibold">Library</span>
          </Link>
        </div>

        {/* Divider */}
        <div className="h-px bg-reader-border mx-5 my-2" />

        {/* Chapter list */}
        <div className="flex-1 overflow-y-auto px-3 pb-6">
          {chapters.length === 0 ? (
            <div className="px-4 py-8 text-center text-reader-text-muted text-sm">
              No chapters yet. Start reading to build your path.
            </div>
          ) : (
            <nav aria-label="Chapters">
              {chapters.map((chapter, index) => {
                const isCurrent = chapter.nodeId === currentNodeId;

                return (
                  <button
                    key={`${chapter.nodeId}-${index}`}
                    onClick={() => {
                      if (!isCurrent) {
                        onSelectChapter(chapter.nodeId, index);
                      }
                      onClose();
                    }}
                    disabled={isCurrent}
                    className={`w-full text-left px-4 py-3 rounded-lg mb-1 transition-colors ${
                      isCurrent
                        ? 'bg-reader-accent cursor-default'
                        : 'hover:bg-reader-bg-card focus:bg-reader-bg-card'
                    }`}
                    aria-current={isCurrent ? 'page' : undefined}
                  >
                    <div
                      className={`text-base font-medium ${isCurrent ? 'text-reader-accent-text' : 'text-reader-text-heading'}`}
                    >
                      Chapter {chapter.chapterNumber}
                    </div>
                    {chapter.chapterTitle && (
                      <div
                        className={`text-sm mt-0.5 truncate ${isCurrent ? 'text-reader-accent-text/90' : 'text-reader-text-muted'}`}
                      >
                        {chapter.chapterTitle}
                      </div>
                    )}
                  </button>
                );
              })}
            </nav>
          )}
        </div>
      </div>
    </div>
  );
}
